import {
  Avatar,
  Table,
  TableBody,
  Badge,
  TableCell,
  TableRow,
  Tooltip,
  IconButton,
  ListItemText,
  Stack,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import { styled } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { UserListHead } from 'src/sections/@dashboard/user';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { CommonHelper } from 'src/utils/commonHelper';
import { PostDetailsHelper } from 'src/utils/postDetailsHelper';
import Facebook from '../../../assets/facebook.svg';
import Instagram from '../../../assets/instagram.svg';
import Twitter from '../../../assets/twitter.svg';
import YouTube from '../../../assets/youtube_social_icon_red 128px.png';
import Pinterest from '../../../assets/pinterest-icon.svg';
import GBusiness from '../../../assets/google-my-business-logo.svg';
import LinkedIn from '../../../assets/linked_in.svg';
import Tiktok from '../../../assets/tiktok.svg';
import { useTranslation } from 'react-i18next';

const SmallAvatar = styled(Avatar)(({ theme }) => ({
  width: 20,
  height: 20,
  overflow: 'hidden',
  backgroundColor: theme.palette.common.white,
}));

const TriggersTable = ({ triggers, type, onStatusChange, onDelete, onEdit }) => {
  const { t: translate } = useTranslation();
  const getAvatar = (service_name) => {
    const avatars = {
      twitter: <SmallAvatar alt="twitter" src={Twitter} />,
      facebook: <SmallAvatar alt="facebook" src={Facebook} />,
      instagram: <SmallAvatar alt="instagram" src={Instagram} />,
      gbusiness: <SmallAvatar alt="gbusiness" src={GBusiness} variant="rounded" />,
      linkedin: <SmallAvatar alt="linkedin" src={LinkedIn} variant="rounded" />,
      tiktok: <SmallAvatar alt="tiktok" src={Tiktok} />,
      youtube: <SmallAvatar alt="youtube" src={YouTube} />,
      pinterest: <SmallAvatar alt="pinterest" src={Pinterest} />,
    };
    return avatars[service_name] || null;
  };

  const getTableHeaders = () => {
    const baseHeaders = [
      { id: 'profile', label: translate('triggers_table.profile', 'Profile'), alignRight: false },
      { id: 'updatedAt', label: translate('triggers_table.updated_at', 'Updated At'), alignRight: false },
      { id: 'createdBy', label: translate('triggers_table.created_by', 'Created By'), alignRight: false },
      { id: 'triggerType', label: translate('triggers_table.trigger_type', 'Trigger Type'), alignRight: false },
    ];

    const additionalHeaders = [];
    if (type === 'comment' || type === 'like') {
      additionalHeaders.push({ id: 'post_scope', label: translate('triggers_table.post_scope', 'Post Scope'), alignRight: false });
    }
    additionalHeaders.push({ id: 'trigger_values', label: translate('triggers_table.trigger_value', 'Trigger Value'), alignRight: false });
    additionalHeaders.push(
      { id: 'status', label: translate('triggers_table.status', 'Status'), alignRight: false },
      { id: 'action', label: translate('triggers_table.action', 'Action'), alignRight: false }
    );

    return [...baseHeaders, ...additionalHeaders];
  };

  return (
    <Table stickyHeader>
      <UserListHead headLabel={getTableHeaders()} rowCount={triggers.length} isShowCheckbox={false} />
      <TableBody>
        {triggers.map((row, i) => (
          <TableRow hover key={i}>
            <StyledComponent.StyledTableCell>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={getAvatar(row.connected_profile_details.social_media)}
                >
                  <Avatar
                    src={row.connected_profile_details.connected_profile_image}
                    alt={row.connected_profile_details.connected_profile_name || row.connected_profile_details.profile_id}
                    sx={{
                      height: 40,
                      width: 40,
                      bgcolor: '#00AB55',
                    }}
                    style={{ color: '#ffffff' }}
                  />
                </Badge>
                <Typography variant="subtitle2" noWrap>
                  {row.connected_profile_details.connected_profile_name || row.connected_profile_details.profile_id}
                </Typography>
              </Stack>
            </StyledComponent.StyledTableCell>
            <StyledComponent.StyledTableCell>
              <ListItemText
                sx={{
                  m: 0,
                  '.MuiListItemText-primary': {
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.875rem',
                  },
                  '.MuiListItemText-secondary': {
                    color: '#637381',
                    fontSize: '0.75rem',
                    textTransform: 'lowercase',
                  },
                }}
                primary={CommonHelper.formatDateAndTime(row.updated_at, 'date')}
                secondary={CommonHelper.formatDateAndTime(row.updated_at, 'time')}
              />
            </StyledComponent.StyledTableCell>
            <StyledComponent.StyledTableCell>
              {PostDetailsHelper.getProfile(row.created_by.name, row.created_by.email, row.created_by.profile_pic)}
            </StyledComponent.StyledTableCell>
            <StyledComponent.StyledTableCell>
              {row.trigger_type}
            </StyledComponent.StyledTableCell>
            {(type === 'comment' || type === 'like') && (
              <StyledComponent.StyledTableCell>
                {row.allPost ? translate('triggers_table.all_posts', 'All Posts') : translate('triggers_table.specific_post', 'Specific Post')}
              </StyledComponent.StyledTableCell>
            )}
            <StyledComponent.StyledTableCell>
              {row.any_trigger_value ? (
                <Chip label={translate('triggers_table.any', 'Any')} color="primary" variant="outlined" size="small" />
              ) : row.trigger_values && row.trigger_values.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {row.trigger_values.map((value, index) => (
                    <Chip
                      key={index}
                      label={value}
                      color="primary"
                      variant="outlined"
                      size="small"
                      sx={{ maxWidth: 150, textOverflow: 'ellipsis', overflow: 'hidden' }}
                    />
                  ))}
                </Box>
              ) : (
                '-'
              )}
            </StyledComponent.StyledTableCell>
            <StyledComponent.StyledTableCell>
              <StyledComponent.SwitchIcon
                sx={{ m: 1 }}
                checked={row.is_enabled}
                onChange={() => onStatusChange(row)}
              />
            </StyledComponent.StyledTableCell>
            <StyledComponent.StyledTableCell>
              <Tooltip title={translate('triggers_table.edit', 'Edit')}>
                <IconButton size="small" onClick={() => onEdit(row)}>
                  <EditIcon color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translate('triggers_table.delete', 'Delete')}>
                <IconButton onClick={() => onDelete(row)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            </StyledComponent.StyledTableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TriggersTable;